import React from 'react';
import { IonContent } from '@ionic/react';
import styled from 'styled-components';
import * as RDBCases from '../../../services/buildfire/rdb/cases';
import PatientInfo from '../PatientInfo';
import SitesList from '../site/SitesList';
import MessageFab from '../../messages/MessageFab';
import { doesCaseHaveUnreadMessage } from '../../../util/case';
import { User } from 'firebase/auth';
import { ParentUser, updateCase } from '../../../api/cloudFunctions';
import MessageProvider from '../../../context/MessageContext';
import { getFunctions } from 'firebase/functions';
import { getDatabase } from 'firebase/database';
import { useFirebase } from '../../../services/firebase';
import { isSuperAdmin } from '../../../util/admin';
import SnackbarContext from '../../../context/SnackbarContext';
import SubmitterInfo from '../SubmitterInfo';
import AdminPaymentInfo from './AdminPaymentInfo';
import ModalLoader from '../../common/ModalLoader';

export interface AdminCaseInfoDetailsProps {
  case: RDBCases.Case;
  currentUser: User;
  isAdmin: boolean;
  actionItems?: React.ReactNode;
  parentAccount?: ParentUser;
  bannerContent?: React.ReactNode | string;
  priorityInfo?: RDBCases.PriorityInfo;
}

const TextSection = styled.div`
  // padding: 8px 8px 0 8px;
  // &:last-child {
  //   padding-bottom: 8px;
  // }
  padding: 16px;
`;

const Content = styled.div`
  margin-bottom: 32px;
`;

const Banner = styled.div`
  padding: 4px;
  // border-radius: 4px;
  background: var(--ion-color-secondary);
  color: white;
`;

const AdminCaseInfoDetails: React.FC<AdminCaseInfoDetailsProps> = ({
  case: userCase,
  isAdmin,
  currentUser,
  actionItems,
  parentAccount,
  children,
  bannerContent,
  priorityInfo,
  ...props
}) => {
  const { app } = useFirebase();
  const { setMessage } = React.useContext(SnackbarContext);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [localCase, setLocalCase] = React.useState(userCase);

  React.useEffect(() => {
    setLocalCase(userCase);
  }, [userCase]);

  const canDeleteSites =
    (isSuperAdmin(currentUser) ||
      currentUser.uid === userCase.acceptedBy?.uid) &&
    localCase.sites.length > 1;

  const handleDeleteSite = async (site: RDBCases.Site, siteIndex: number) => {
    setIsDeleting(true);
    try {
      const functions = getFunctions(app);
      const db = getDatabase(app);

      const invoice = await RDBCases.getInvoice(db, localCase.id);

      const updatedSites = localCase.sites.filter(
        (_, index) => index !== siteIndex
      );

      const result = await updateCase(functions, {
        case: {
          ...localCase,
          sites: updatedSites,
        },
        user: {
          id: localCase.uid,
          name: localCase.user.displayName,
          email: localCase.user.email,
        },
        paymentMethodId: invoice?.paymentMethodId || undefined,
        setupIntentId: invoice?.setupIntentId || undefined,
      });

      setLocalCase(result.case);
      setMessage({ text: 'Site deleted successfully' });
    } catch (error) {
      console.error('Error deleting site:', error);
      setMessage({ text: 'Failed to delete site. Please try again.' });
    } finally {
      setIsDeleting(false);
    }
  };

  const canMessage = currentUser.uid === userCase.uid || isAdmin;
  const isUnread =
    canMessage && doesCaseHaveUnreadMessage(userCase, currentUser!, isAdmin);
  return (
    <>
      <IonContent>
        <Content>
          {/* {bannerContent && <Banner>{bannerContent}</Banner>} */}
          {bannerContent}

          <TextSection>
            <SubmitterInfo
              case={localCase}
              clinicName={localCase.user.displayName}
              clinicEmail={localCase.user.email}
              isAdmin={isAdmin}
              priorityInfo={priorityInfo}
            />
            <AdminPaymentInfo case={localCase} />
            <PatientInfo case={localCase} includeName />
          </TextSection>
          <SitesList
            case={localCase}
            sites={localCase.sites}
            userId={localCase.uid}
            isDeleteable={canDeleteSites && !isDeleting}
            onDeleted={handleDeleteSite}
          />
          {children}
        </Content>
      </IonContent>
      {canMessage && (
        <MessageProvider>
          <MessageFab unread={isUnread} />
        </MessageProvider>
      )}
      {isDeleting && (
        <ModalLoader progressText="Deleting site..." onClose={() => {}} />
      )}
    </>
  );
};

export default AdminCaseInfoDetails;
